import { Component, HostBinding, OnInit, OnDestroy, ViewChild, isDevMode } from '@angular/core';
import { ActivatedRoute, ParamMap, Router, NavigationEnd } from '@angular/router';
import { MatSidenavContent } from '@angular/material/sidenav';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { format, isValid } from 'date-fns';

import { ApplicationService, SchoolNomineeInfo } from 'services/application/application.service';
import { AuthService } from 'services/authentication/auth.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { DialogService, ConfirmationDialogOptions } from 'services/dialogs/dialogs.service';
import { ScrollService } from 'services/scroll/scroll.service';
import { UserInfo, UserService } from 'services/user/user.service';
import { BUILD_NOTES, BUILD_TIME, VERSION_NUMBER } from 'services/version/version.service';
import { NavMenuItem } from 'ui/navmenu/navmenu.component';
import { SettingsService } from '../services/settings/settings.service';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
})

export class AppComponent implements OnInit, OnDestroy {
	@HostBinding('class.smalldisplay') SmallDisplay: boolean;
	@ViewChild(MatSidenavContent) sidenavContent: MatSidenavContent;

	private _subRelease: Subject<void> = new Subject<void>();
	AboutFn: Function;
	LogoutFn: Function;

	AwardInfo: AwardProgramInfo;
	AwardURL: string;
	BuildNotes: string;
	BuildTime: string;
	DevSite: boolean;
	LastSizeCode: string;
	MenuItems: NavMenuItem[];
	MenuOpen: boolean;
	SizeCode: string;
	SmallSize: boolean;
	UpdateAvailable: boolean;
	User: UserInfo;
	VersionLoaded: string;
	ViewReady: boolean;
	NomByLocation: SchoolNomineeInfo[] = [];

	constructor(
		private appService: ApplicationService,
		private authService: AuthService,
		private awardsService: AwardsService,
		private dialogService: DialogService,
		private media: MediaObserver,
		private route: ActivatedRoute,
		private router: Router,
		private scrollSvc: ScrollService,
		private settingsSvc: SettingsService,
		private userService: UserService
	) { }

	ngOnDestroy() {
		this._subRelease.next();
		this._subRelease.complete();
		this.authService.CancelAuthTokenRefreshing();
	}

	ngOnInit() {
		const self = this;
		self.AboutFn = self.About.bind(self);
		self.AwardInfo = null;
		self.BuildNotes = BUILD_NOTES;
		self.LogoutFn = self.Logout.bind(self);
		self.MenuOpen = false;
		self.SmallDisplay = false;
		self.UpdateAvailable = false;
		self.VersionLoaded = VERSION_NUMBER;
		self.ViewReady = false;

		const btime = new Date(BUILD_TIME);
		if (isValid(btime)) {
			self.BuildTime = format(btime, 'MMM. dd, yyyy h:mm a');
		}

		this.settingsSvc.DevSiteSubject.pipe(takeUntil(this._subRelease)).subscribe(dev => {
			this.DevSite = dev;
		});

		// Check for cancelled router navigation
		self.router.events.pipe(
			takeUntil(self._subRelease),
			filter(event => event instanceof NavigationEnd),
			mergeMap(() => {
				return self.route.firstChild ? self.route.firstChild.paramMap : self.route.paramMap;
			}),
			switchMap((params: ParamMap) =>
				of({ System: params.get('system') })
			)
		).subscribe(params => {
			if (params != null && params.System != null) {
				self.AwardURL = params.System.toUpperCase();

				// Try to find the award info going with the URL path.
				self.AwardInfo = (self.appService.AvailableAwardPrograms || []).find(w => w.URLPath === self.AwardURL);

				// Update the application wide setting so it ripples outward to all children components.
				self.awardsService.CurrentAwards = self.AwardInfo;

				if (self.User && self.AwardInfo) {
					self.setNavMenu();
				}
			}
		});

		// Subscribe to checks for a version upgrade
		self.authService.HasVersionUpdate.pipe(
			takeUntil(self._subRelease)
		).subscribe(value => {
			// Set the upgrade alert banner (or not)
			self.UpdateAvailable = value;
		});

		// Subscribe to media changes
		self.media.media$.pipe(takeUntil(self._subRelease)).subscribe((change: MediaChange) => {
			self.SmallSize = self.media.isActive('xs') || self.media.isActive('sm');
			self.SmallDisplay = self.SmallSize;
			self.SizeCode =
				change.mqAlias === 'xl'
					? 'xl'
					: change.mqAlias === 'lg'
						? 'lg'
						: change.mqAlias === 'md'
							? 'md'
							: change.mqAlias === 'sm'
								? 'sm'
								: 'xs';

			if (self.SmallSize) {
				self.MenuOpen = false;
			} else {
				self.MenuOpen = true;
			}
		});

		self.SmallSize = self.media.isActive('xs') || self.media.isActive('sm');
		self.SmallDisplay = self.SmallSize;
		self.SizeCode = self.media.isActive('xl')
			? 'xl'
			: self.media.isActive('lg')
				? 'lg'
				: self.media.isActive('md')
					? 'md'
					: self.media.isActive('sm')
						? 'sm'
						: 'xs';
		self.LastSizeCode = self.SizeCode;

		if (self.SmallSize) {
			self.MenuOpen = false;
		} else {
			self.MenuOpen = true;
		}

		// Subscribe to scroll events, so we can scroll up the sidenav content (if needed)
		self.scrollSvc.ScrollEvent.pipe(takeUntil(self._subRelease)).subscribe(
			scrollAmt => {
				if (self.sidenavContent) {
					self.sidenavContent.scrollTo({
						top: scrollAmt || 0,
					});
				}
			}
		);

		self.appService.GetAwardPrograms().pipe(takeUntil(this._subRelease)).subscribe(v => {
			// The system responded. We can continue with a successful load process.
			// Try to find the award info going with the URL path.
			if (self.AwardURL != null) {
				self.AwardInfo = (self.appService.AvailableAwardPrograms || []).find(w => w.URLPath === self.AwardURL);

				// Update the application wide setting so it ripples outward to all children components.
				self.awardsService.CurrentAwards = self.AwardInfo;
			}

			// Subscribe to the user information, and keep it updated, along with the nav menu.
			combineLatest(self.userService.UserSubject, self.appService.nomByLocationSubject).pipe(
				takeUntil(self._subRelease),
				map(_p => {
					return {
						UserInfo: _p[0],
						nomByLoc: _p[1],
					};
				})
			).subscribe(_datapack => {
				self.User = _datapack.UserInfo;
				self.NomByLocation = [];
				let nomInformation = _datapack.nomByLoc;

				if (nomInformation) {
					for (let n of nomInformation.NomineeType) {
						let nInfo = new SchoolNomineeInfo();

						nInfo.NomineeInfo = nomInformation.Nominees.find(x => x.NomineeTypeID === n.NomineeTypeID && x.GradeLevel === n.GradeLevel);

						switch (n.GradeLevel) {
							case "STUDENTELEM":
								n.GradeDisplay = "Elementary School";
								break;
							case "STUDENTMIDDLE":
								n.GradeDisplay = "Middle School";
								break;
							case "STUDENTHIGH":
								n.GradeDisplay = "High School";
								break;
							default:
								n.GradeDisplay = null;
						}

						nInfo.NomineeType = n;
						self.NomByLocation.push(nInfo);
					}
				}

				if (self.User && self.AwardInfo) {
					self.setNavMenu();
				}
			});
		});
	}

	setNavMenu() {
		let self = this;
		const instMenus = <NavMenuItem[]>[];
		const formsMenus = <NavMenuItem[]>[];
		const navitems: NavMenuItem[] = [];
		const schMenus = [
			{
				MenuText: 'Select Nominees',
				// MenuIcon: 'people',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: [
					'~/managenominees',
				],
				ActionRoute: [
					'~/managenominees'
				]
			},
			{
				MenuText: 'Select Designees',
				// MenuIcon: 'supervisor_account',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: [
					'~/managedesignees',
				],
				ActionRoute: [
					'~/managedesignees'
				]
			}
		];

		for (let nom of self.NomByLocation) {
			instMenus.push({
				MenuText: `${nom.NomineeType.ShortName
					} Instructions`,
				// MenuIcon: 'info',
				AllowedRoles: [
					'Principal',
					'AssistantPrincipal',
					'Counselor',
					'Designee',
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: [
					`~/instructions/${nom.NomineeType.NomineeTypeID
					}`
				],
				ActionRoute: [
					'~/instructions',
					nom.NomineeType.NomineeTypeID
				],
				DesigneeRights: [
					'EDITFORMS',
					'EDITSCHOOL',
					'EDITTOY',
					'MAKENOMINATIONS',
					'UPLOADVIDEOS'
				]
			});
		}

		if (self.NomByLocation && self.NomByLocation.length > 0 && self.NomByLocation.some(n => n.NomineeInfo != null)) {
			for (let nom of self.NomByLocation) {
				let nomType = nom.NomineeType;
				if (nomType.ShortName == "TOY") {
					formsMenus.push({
						MenuText: `${nomType.Name} Form`,
						// MenuIcon: 'description',
						AllowedRoles: [
							'Principal',
							'AssistantPrincipal',
							'Designee',
							'SystemAdmin',
							'Developer'
						],
						LinkedStates: [
							`~/nominationform/${nomType.NomineeTypeID
							}`
						],
						ActionRoute: [
							'~/nominationform',
							nomType.NomineeTypeID
						],
						DesigneeRights: [
							'EDITTOY',
							'EDITFORMS'
						],
						Disabled: (nom.NomineeInfo == null)
					});
				}
				else if (nomType.ShortName == "SRE") {
					formsMenus.push({
						MenuText: `${nomType.Name} Form`,
						// MenuIcon: 'description',
						AllowedRoles: [
							'Principal',
							'AssistantPrincipal',
							'Designee',
							'SystemAdmin',
							'Developer'
						],
						LinkedStates: [
							`~/nominationform/${nomType.NomineeTypeID
							}`,
						],
						ActionRoute: [
							'~/nominationform',
							nomType.NomineeTypeID,
						],
						DesigneeRights: [
							'EDITSCHOOL',
							'EDITFORMS'
						],
						Disabled: (nom.NomineeInfo == null)
					});
				}
				else if (nomType.GradeLevel) {
					formsMenus.push({
						MenuText: `${nomType.Name} ${nomType.GradeDisplay} Form`,
						// MenuIcon: 'description',
						AllowedRoles: [
							'Principal',
							'AssistantPrincipal',
							'Counselor',
							'Designee',
							'SystemAdmin',
							'Developer'
						],
						LinkedStates: [
							`~/nominationform/${nomType.NomineeTypeID,
							nomType.GradeLevel
							}`
						],
						ActionRoute: [
							'~/nominationform',
							nomType.NomineeTypeID,
							nomType.GradeLevel
						],
						DesigneeRights: [
							'EDITFORMS'
						],
						Disabled: (nom.NomineeInfo == null)
					});
				}
				else {
					formsMenus.push({
						MenuText: `${nomType.Name} Form`,
						// MenuIcon: 'description',
						AllowedRoles: [
							'Principal',
							'AssistantPrincipal',
							'Counselor',
							'Designee',
							'SystemAdmin',
							'Developer'
						],
						LinkedStates: [
							`~/nominationform/${nomType.NomineeTypeID
							}`
						],
						ActionRoute: [
							'~/nominationform',
							nomType.NomineeTypeID
						],
						DesigneeRights: [
							'EDITFORMS'
						],
						Disabled: (nom.NomineeInfo == null)
					});
				}
			}
		}

		//ALL people
		navitems.push({
			MenuText: 'Home',
			MenuIcon: 'home',
			AllowedRoles: ['all'],
			LinkedStates: ['~/home'],
			IsHome: true,
			ActionRoute: ['~/home']
		});

		//NOT Hall of Fame
		if (self.AwardInfo.ShortName != "HOF") {
			navitems.push(
				{
					MenuText: 'Select Nominees',
					MenuIcon: 'people',
					AllowedRoles: [
						'Principal',
						'AssistantPrincipal',
						'Counselor',
						'Designee'
					],
					LinkedStates: [
						'~/managenominees'
					],
					ActionRoute: [
						'~/managenominees'
					],
					DesigneeRights: [
						'MAKENOMINATIONS'
					]
				},
				{
					MenuText:
						'Select Designees',
					MenuIcon:
						'supervisor_account',
					AllowedRoles: [
						'Principal',
						'AssistantPrincipal'
					],
					LinkedStates: [
						'~/managedesignees'
					],
					ActionRoute: [
						'~/managedesignees'
					]
				},
				{
					MenuText: 'Choose School/Year',
					MenuIcon: 'school',
					AllowedRoles: [
						'SystemAdmin',
						'Developer'
					],
					LinkedStates: ['~/schools'],
					ActionRoute: ['~/schools']
				},
				{
					MenuText: 'Administator Tools',
					MenuIcon: 'people',
					AllowedRoles: [
						'SystemAdmin',
						'Developer'
					],
					Submenus: schMenus
				},
				{
					MenuText: 'Instructions',
					MenuIcon: 'info',
					AllowedRoles: [
						'Principal',
						'AssistantPrincipal',
						'Counselor',
						'Designee',
						'SystemAdmin',
						'Developer'
					],
					Submenus: instMenus,
					DesigneeRights: [
						'EDITFORMS',
						'EDITSCHOOL',
						'EDITTOY',
						'MAKENOMINATIONS',
						'UPLOADVIDEOS'
					]
				},
				{
					MenuText: 'Forms',
					MenuIcon: 'description',
					AllowedRoles: [
						'Principal',
						'AssistantPrincipal',
						'Counselor',
						'Designee',
						'SystemAdmin',
						'Developer'
					],
					Disabled: (formsMenus.length < 1),
					Submenus: formsMenus,
					DesigneeRights: [
						'EDITFORMS'
					]
				}
			);

			if (self.NomByLocation.some(n => n.NomineeType.AttachmentTypes.some(a => a.Code === 'VIDEO'))) {
				// Add the attachments / video tributes menu
				navitems.push({
					MenuText: 'Video Tributes',
					MenuIcon: 'videocam',
					AllowedRoles: [
						'Principal',
						'AssistantPrincipal',
						'Designee',
						'SystemAdmin',
						'Developer'
					],
					LinkedStates: ['~/attachments'],
					ActionRoute: ['~/attachments'],
					DesigneeRights: [
						'UPLOADVIDEOS'
					]
				});
			}

			if (self.AwardInfo && self.AwardInfo.HasRSVP) {
				const rsvpMenus = <NavMenuItem[]>[];
				if (self.NomByLocation && self.NomByLocation.length > 0 && self.NomByLocation.some(n => n.NomineeInfo != null)) {
					for (let nom of self.NomByLocation) {
						let nomType = nom.NomineeType;
						if (nomType.GradeLevel) {
							rsvpMenus.push({
								MenuText: `${nomType.Name} ${nomType.GradeDisplay} RSVPs`,
								// MenuIcon: 'description',
								AllowedRoles: [
									'Principal',
									'AssistantPrincipal',
									'Counselor',
									'Designee',
									'SystemAdmin',
									'Developer'
								],
								LinkedStates: [
									`~/rsvp/${nomType.NomineeTypeID,
									nomType.GradeLevel
									}`
								],
								ActionRoute: [
									'~/rsvp',
									nomType.NomineeTypeID,
									nomType.GradeLevel
								],
								DesigneeRights: [
									'MAKERSVPS'
								],
								Disabled: (nom.NomineeInfo == null)
							});
						}
						else {
							rsvpMenus.push({
								MenuText: `${nomType.Name} RSVPs`,
								// MenuIcon: 'description',
								AllowedRoles: [
									'Principal',
									'AssistantPrincipal',
									'Counselor',
									'Designee',
									'SystemAdmin',
									'Developer'
								],
								LinkedStates: [
									`~/rsvp/${nomType.NomineeTypeID
									}`
								],
								ActionRoute: [
									'~/rsvp',
									nomType.NomineeTypeID
								],
								DesigneeRights: [
									'MAKERSVPS'
								],
								Disabled: (nom.NomineeInfo == null)
							});
						}
					}
				}

				// Add RSVPs
				navitems.push({
					MenuText: 'RSVPs',
					MenuIcon: 'event_seat',
					AllowedRoles: [
						'Principal',
						'AssistantPrincipal',
						'Counselor',
						'Designee',
						'SystemAdmin',
						'Developer'
					],
					Disabled: (rsvpMenus.length < 1),
					Submenus: rsvpMenus,
					DesigneeRights: [
						'MAKERSVPS'
					]
				});
			}

			let nom = self.User.Roles.find(x => x.RoleName == "Nominee");
			if (nom != null) {
				// Nominee type specific menu entries
				navitems.push(
					{
						MenuText: 'Instructions',
						MenuIcon: 'info',
						AllowedRoles: ['Nominee'],
						LinkedStates: ['~/instructions'],
						ActionRoute: ['~/instructions', nom.NomineeType]
					},
					{
						MenuText: 'Nomination Forms',
						MenuIcon: 'description',
						AllowedRoles: ['Nominee'],
						LinkedStates: ['~/nominationform'],
						ActionRoute: ['~/nominationform', nom.NomineeType]
					}
				);
			}
		}

		if (self.AwardInfo.IsJudged) {
			navitems.push({
				MenuText: (self.User.CurrentRole === 'Judge') ? 'Judging' : 'Judge Impersonation',
				MenuIcon: 'gavel',
				AllowedRoles: [
					'Judge',
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: ['~/judgenominees'],
				ActionRoute: ['~/judgenominees']
			});
		}

		navitems.push({
			MenuText: 'Reports & Exports',
			MenuIcon: 'file_download',
			AllowedRoles: [
				'SystemAdmin',
				'Developer'
			],
			LinkedStates: ['~/reports'],
			ActionRoute: ['~/reports']
		});

		if (self.AwardInfo.IsJudged) {
			navitems.push({
				MenuText: 'Finalize Nominees',
				MenuIcon: 'gavel',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: ['~/finalizenominees'],
				ActionRoute: ['~/finalizenominees']
			});
		}

		// System Tools
		let sysMenus = [
			{
				MenuText: 'School Eligibility',
				// MenuIcon: 'event',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: ['~/schooleligibility'],
				ActionRoute: ['~/schooleligibility']
			},
			{
				MenuText: 'Edit School Groups',
				// MenuIcon: 'school',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: ['~/schoolgroups'],
				ActionRoute: ['~/schoolgroups']
			},
			{
				MenuText: 'Edit Window Dates',
				// MenuIcon: 'event',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: ['~/windows'],
				ActionRoute: ['~/windows']
			},
			{
				MenuText: 'Judge Tool',
				// MenuIcon: 'event',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: ['~/judgetool'],
				ActionRoute: ['~/judgetool']
			},
			{
				MenuText: 'Dev Tools',
				// MenuIcon: 'event',
				AllowedRoles: [
					'Developer'
				],
				LinkedStates: ['~/devtools'],
				ActionRoute: ['~/devtools']
			},
			{
				MenuText: 'Award Status',
				// MenuIcon: 'event',
				AllowedRoles: [
					'SystemAdmin',
					'Developer'
				],
				LinkedStates: ['~/status'],
				ActionRoute: ['~/status']
			}
		];

		if (!isDevMode()) {
			sysMenus = sysMenus.filter(f => f.MenuText != 'Dev Tools');
		}

		navitems.push({
			MenuText: 'System Settings',
			MenuIcon: 'settings',
			AllowedRoles: [
				'SystemAdmin',
				'Developer'
			],
			Submenus: sysMenus,
		});

		navitems.push(
			{
				MenuText: 'Logout',
				MenuIcon: 'exit_to_app',
				AllowedRoles: ['all'],
				IsLogout: true
			}
		);

		// Set the property
		self.MenuItems = navitems;
		self.ViewReady = true;
	}

	About() {
		const self = this;

		if (self.SmallSize) {
			self.MenuOpen = false;
		}

		let msg =
			`The Polk Awards System is a product of the Polk County Public Schools system in conjunction with ` +
			`the Public Relations department. The system provides a single point-of-reference location for all activities ` +
			`related to the selection of annual awards nominations and applications.<br /><br />` +
			`<strong>Polk Awards Version: </strong>${self.VersionLoaded}<br />
        <strong>Build Date/Time: </strong>${self.BuildTime}`;

		if (self.BuildNotes) {
			msg +=
				`<br /><strong>Build Notes: </strong><br />` + self.BuildNotes;
		}

		return self.dialogService.OpenMessageBoxDialog({
			TitleText: 'About Polk Awards System',
			MessageText: msg,
			height: self.BuildNotes ? '450px' : '400px',
		});
	}

	GetUpdate() {
		// Get a confirmation
		this.dialogService
			.OpenConfirmationDialog({
				TitleText: 'Site Update Available',
				MessageText:
					`An update is available to the Polk Awards System. Updates typically fix issues reported ` +
					`by users or offer new features. If you are encountering problems with the site, it is recommended that ` +
					`you load the update for the website at this time.<br /><br /><strong>Note: </strong>Loading the update ` +
					`will refresh your page and you will lose any unsubmitted changes.`,
				OKText: 'Load Update',
				CancelText: 'CANCEL',
			}).pipe(takeUntil(this._subRelease))
			.subscribe(res => {
				if (res === true) {
					// User is opting to load the update. Refresh the page without caching.
					location.reload();
				}
			});
	}

	GoHome() {
		if (
			this.userService.User == null ||
			this.userService.User.AuthToken == null
		) {
			this.router.navigate(['/login']);
		} else {
			this.router.navigate(['/home']);
		}
	}

	Logout() {
		// Pop up a confirmation box to confirm desire to logout.
		const opts: ConfirmationDialogOptions = {
			TitleText: 'Confirmation Requested',
			MessageText:
				'You are about to log out of the Polk Awards System. Are you sure you wish to exit at this time?',
			OKText: 'Yes - Log Out',
			CancelText: 'No - Stay Logged In',
		};

		return this.dialogService.OpenConfirmationDialog(opts).pipe(
			tap(result => {
				if (result === true) {
					// User is opting to log out. Proceed.
					this.authService.Logout(null, null);
				}
			})
		);
	}

	Navigate(event) {
		if (this.SmallSize) {
			this.MenuOpen = false;
		}
	}

	ToolbarAbout() {
		this.About()
			.pipe(takeUntil(this._subRelease))
			.subscribe();
	}

	ToolbarLogout() {
		this.Logout()
			.pipe(takeUntil(this._subRelease))
			.subscribe();
	}

	ToggleNavMenu() {
		this.MenuOpen = !this.MenuOpen;
	}

}
